import React, { useEffect, useState, useRef, useCallback } from "react";
import Script from "react-load-script";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { checkFormValidity, countryChange, googleAddressUpdate } from "../../form-store/action-types";
import { onChangeEvent } from "../reactive-events/onChange";
import countries from "../../constants/countries";
import { isCountryWithStateOptions, resetStateAndDependentField, toggleCountyRequiredField, updateCounty, updateStates, updateZipCodes } from "../../util/utility";
import states from "../../constants/states";
import { onStateChange, resetStateField } from "../reactive-events/onSelect";
import { addressFromGoogle } from "../../form-store/app-reducer";

const GaddressFields = props => {
  const address = {};
  const key = process.env.REACT_APP_GAPI_KEY;
  let autocomplete;
  const dispatch = useDispatch();
  const options = useSelector(({ app }) => app.options, shallowEqual);
  const formObj = useSelector(({ form }) => form[form.currentForm], shallowEqual);
  const [customError, setCustomError] = useState('');
  const [value, setValue] = useState('');
  const inputRef = useRef({});

  const validateInput = useCallback((value) => {
    setCustomError('');
    
    // Check for forbidden special characters
    if (value && !/^(?!.*[ÑñÍíÁáÉéÓóÚúÜüßØøÇç]).*$/.test(value)) {
      const errorMsg = 'Address cannot contain special characters like Ñ, Í, Á, É, Ó, Ú, Ü, ß, Ø, Ç and their lowercase variants.';
      setCustomError(errorMsg);
      if (inputRef.current) {
        inputRef.current.setCustomValidity(errorMsg);
      }
      return;
    }
    
    if (value && props.pattern) {
      const pattern = new RegExp(props.pattern);
      if (!pattern.test(value)) {
        const errorMsg = props.errortext || 'Invalid characters detected.';
        setCustomError(errorMsg);
        if (inputRef.current) {
          inputRef.current.setCustomValidity(errorMsg);
        }
        return;
      }
    }
    
    if (inputRef.current) {
      inputRef.current.setCustomValidity('');
    }
  }, [props.pattern, props.errortext]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setValue(val);
    validateInput(val);
    // Save the Google address input value to form state for persistence
    dispatch(onChangeEvent('googleAddress', val));
  };

  // Effect to sync form state with local state
  useEffect(() => {
    // Check if there's a persisted Google address value in the form state
    const googleAddressValue = formObj && formObj.googleAddress ? formObj.googleAddress : '';
    if (googleAddressValue !== value && googleAddressValue) {
      setValue(googleAddressValue);
      if (inputRef.current) {
        inputRef.current.value = googleAddressValue;
      }
      validateInput(googleAddressValue);
    }
  }, [formObj, value, validateInput]);

  // Effect to validate the input value when component mounts or when validation rules change
  useEffect(() => {
    if (inputRef.current && inputRef.current.value) {
      validateInput(inputRef.current.value);
    }
  }, [validateInput]);

  useEffect(() => {
    if (options.length) {
      dispatch(updateZipCodes());
      dispatch(updateCounty());
    }
  }, [options, dispatch]);

  const handleScriptLoad = () => {
    // Initialize Google Autocomplete
    /*global google*/ // To disable any eslint 'google not defined' errors

    // const countryCodes = countries.map(country => country.value);

    // autocomplete = new google.maps.places.Autocomplete(
    //   document.getElementById("autocomplete"),
    //   { componentRestrictions: { country: countryCodes } }
    // );

    autocomplete = new google.maps.places.Autocomplete(document.getElementById("autocomplete"))
    
    // Fire Event when a suggested name is selected
    autocomplete.addListener("place_changed", handlePlaceSelect);
  };

  const handlePlaceSelect = () => {
    let addressObject = autocomplete.getPlace();
    let gAddrress = addressObject.address_components;
    dispatch(resetStateField());
    let googleAddrFields = {
      streetNumber: "street_number",
      streetAddress1: "route",
      streetAddress2: "sublocality_level_2",
      streetAddress3: "sublocality_level_1",
      streetAddress4: "sublocality_level_3",
      city: "locality",
      state: "administrative_area_level_1",
      postalCode: "postal_code",
      countryCode: "country",
      county: "administrative_area_level_2",
    };

    let addressObj = {};

    if (gAddrress) {
      dispatch(resetStateAndDependentField())
      gAddrress.forEach(addressItem => {
        if (addressItem.types[0] === googleAddrFields.streetNumber) {
          address.streetNumber = addressItem.long_name;
        }
        if (addressItem.types[0] === googleAddrFields.streetAddress1) {
          addressObj.addressLine1 = addressItem.long_name;
        }
        if (addressItem.types[0] === googleAddrFields.streetAddress2) {
          addressObj.addressLine2 = addressItem.long_name;
        }
        if (addressItem.types[0] === googleAddrFields.streetAddress3) {
          addressObj.addressLine3 = addressItem.long_name;
        }
        if (addressItem.types[0] === googleAddrFields.streetAddress4) {
          addressObj.addressLine4 = addressItem.long_name;
        }

        if (addressItem.types[0] === googleAddrFields.city) {
          addressObj.city = addressItem.long_name;
        }
        if (addressItem.types[0] === googleAddrFields.countryCode) {
          // addressObj.country = addressItem.short_name;
          addressObj.countryCode = addressItem.short_name;
        }
        if (addressItem.types[0] === googleAddrFields.state) {
          addressObj.state = addressItem.short_name;
          // addressObj.stateCode = addressItem.long_name;
        }

        if (addressItem.types[0] === googleAddrFields.postalCode) {
          addressObj.postalCode = addressItem.long_name;
        }
        //for county
        if (addressItem.types[0] === googleAddrFields.county) {
          addressObj.county = addressItem.long_name.replace(" County", "");
        }
      });
    }
    if (isCountryWithStateOptions(address.countryCode)) {
      address.stateCode = address.state;
    }
    // Street Number Changes
    if (addressObj.addressLine1) {
      addressObj.addressLine1 = address.streetNumber ? address.streetNumber + " " + addressObj.addressLine1 : addressObj.addressLine1;
    }

    if (address.countryCode === "AR") {
      address.stateCode = address.state.toUpperCase();
    }

    if (address.countryCode === "IN") {
      address.stateCode = address.stateCode === "OD" ? "OR" : address.stateCode;
    }

    if (address) {
      let _stateList = states[addressObj.countryCode] || [];
      dispatch(updateStates(addressObj.countryCode));
      if (addressObj.countryCode === "US") dispatch(onStateChange(addressObj.state.toUpperCase()));
      addressObj.state = _stateList.find(st => st.value.toUpperCase() === addressObj.state.toUpperCase()) || addressObj.state;
      addressObj.countryCode = countries.find(ctry => ctry.value === addressObj.countryCode);

      dispatch(toggleCountyRequiredField(addressObj.countryCode));
      dispatch(countryChange(addressObj.countryCode));
      dispatch(googleAddressUpdate(addressObj));
      dispatch(addressFromGoogle(addressObj));

      // Validate the populated address after a short delay to ensure DOM is updated
      setTimeout(() => {
        dispatch(checkFormValidity());
        if (inputRef.current && inputRef.current.value) {
          const googleInputValue = inputRef.current.value;
          setValue(googleInputValue);
          validateInput(googleInputValue);
          // Save the Google address input value to form state for persistence
          dispatch(onChangeEvent('googleAddress', googleInputValue));
        }
      }, 10);
    }
  };

  return (
    <div style={{ gridArea: "gAddress" }}>
      <Script url={`https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`} onLoad={handleScriptLoad} />
      <p className="sisp-label">{props.label}</p>
      <input 
        ref={inputRef}
        id="autocomplete" 
        label="Address" 
        className="sisp-input" 
        placeholder="Enter your address" 
        margin="normal" 
        value={value}
        pattern={props.pattern}
        minLength={props.minLength}
        maxLength={props.maxLength}
        onChange={handleInputChange}
        onBlur={handleInputChange}
      />
      <p className='sisp-input-helper'>{customError || props.helpertext || ""}</p>
    </div>
  );
};

export default GaddressFields;
